import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';

export default function CSCOBarChart(lProps) {
    const [mChartData, setChartData] = useState({});
    const [mChartOptions, setChartOptions] = useState({});

    useEffect(() => {
        const lDocumentStyle = getComputedStyle(document.documentElement);
        const lTextColor = lDocumentStyle.getPropertyValue('--text-color');
        const lSurfaceBorder = lDocumentStyle.getPropertyValue('--surface-border');
        let lChartData = {};

        let lData = lProps.pDataCollection || lProps.pData;
        if (lData && ((lProps.pChartData && lProps.pChartData.chart_data_field_) || (lProps.pReportChartProperties))) {
            if (lProps.pChartData && lProps.pChartData.chart_var_field_) {
                lChartData.labels = [];
                lChartData.datasets = [];
                if (lProps.pChartData.hasOwnProperty('chart_data_type_') && (lProps.pChartData.chart_data_type_ === 'VARIABLE')) {
                    let lChartDataField = lProps.pChartData.chart_data_field_[0];
                    let lChartLabelDataMap = {};
                    for (let lInd = 0; lInd < lData.length; lInd++) {
                        let lObj = lData[lInd];
                        let lDataLabel = lObj[lProps.pChartData.chart_data_label_];
                        let lData1 = lChartLabelDataMap[lDataLabel];
                        if (!lData1) {
                            lData1 = {};
                            lChartLabelDataMap[lDataLabel] = lData1;
                            lData1.data = [];
                        }

                        if (!lChartData.labels.includes(lObj[lProps.pChartData.chart_var_field_])) {
                            lChartData.labels.push(lObj[lProps.pChartData.chart_var_field_]);
                        }

                        lData1.data.push(lObj[lChartDataField]);
                    }

                    let lKeys = Object.keys(lChartLabelDataMap);
                    for (let lKeyInd = 0; lKeyInd < lKeys.length; lKeyInd++) {
                        let lDataObj = {}
                        lDataObj.label = lKeys[lKeyInd];
                        lDataObj.data = (lChartLabelDataMap[lKeys[lKeyInd]]).data;
                        lChartData.datasets.push(lDataObj);
                    }
                }
                else {
                    for (let lDataInd = 0; lDataInd < lProps.pChartData.chart_data_field_.length; lDataInd++) {
                        let lChartDataField = lProps.pChartData.chart_data_field_[lDataInd];
                        let lChartDataLabel = lProps.pChartData.chart_data_label_[lDataInd];
                        let lDataObj = {}
                        lDataObj.label = lChartDataLabel;
                        lDataObj.data = [];
                        for (let lInd = 0; lInd < lData.length; lInd++) {
                            let lObj = lData[lInd];
                            lDataObj.data.push(lObj[lChartDataField]);

                            if (lDataInd === 0) {
                                lChartData.labels.push(lObj[lProps.pChartData.chart_var_field_]);
                            }
                        }

                        lChartData.datasets.push(lDataObj);
                    }
                }
            }
            else if (lProps.pReportChartProperties) {
                const propsArr = lProps.pReportChartProperties;

                // 👉 Detect grouped configs via chart_id_
                const chartIdProps = propsArr.filter(p => p.property_name_ === 'chart_id_');

                // MULTI-DATASET MODE (using chart_id_ + parent_ref_id_)
                if (chartIdProps.length > 0) {
                    lChartData.labels = [];
                    lChartData.datasets = [];

                    // Build config for each chart_id_ group
                    const groups = chartIdProps.map(idProp => {
                        const group = {
                            id: idProp.id_,
                            chartId: idProp.value_,
                            varField: null,
                            dataField: null,
                            label: null
                        };

                        // children share parent_ref_id_ = idProp.id_
                        const children = propsArr.filter(p => p.parent_ref_id_ === idProp.id_);
                        children.forEach(ch => {
                            switch (ch.property_name_) {
                                case 'chart_var_field_':
                                    group.varField = ch.value_;
                                    break;
                                case 'chart_data_field_':
                                    group.dataField = ch.value_;
                                    break;
                                case 'chart_data_label_':
                                    group.label = ch.value_;
                                    break;
                                default:
                                    break;
                            }
                        });

                        return group;
                    });

                    if (groups.length > 0) {
                        // Use varField from the first group as common X-axis
                        const varField = groups[0].varField;

                        // Build labels once from data
                        if (varField) {
                            for (let i = 0; i < lData.length; i++) {
                                const rec = lData[i];
                                const label = rec[varField];
                                if (label != null && !lChartData.labels.includes(label)) {
                                    lChartData.labels.push(label);
                                }
                            }
                        }

                        // Build one dataset per group
                        groups.forEach(group => {
                            if (!group.dataField) return;

                            const ds = {
                                label: group.label || group.dataField,
                                data: []
                            };

                            for (let i = 0; i < lData.length; i++) {
                                const rec = lData[i];
                                ds.data.push(rec[group.dataField]);
                            }

                            lChartData.datasets.push(ds);
                        });
                    }
                }
                // SINGLE-DATASET (legacy) MODE – no chart_id_ rows, same as earlier logic
                else {
                    const cfg = {};
                    propsArr.forEach(p => {
                        const name = p.property_name_?.trim();
                        const value = p.value_?.trim();
                        if (!name) return;

                        if (cfg[name]) {
                            if (Array.isArray(cfg[name])) cfg[name].push(value);
                            else cfg[name] = [cfg[name], value];
                        } else {
                            cfg[name] = value;
                        }
                    });

                    if (cfg.chart_data_field_ && !Array.isArray(cfg.chart_data_field_))
                        cfg.chart_data_field_ = [cfg.chart_data_field_];
                    if (cfg.chart_data_label_ && !Array.isArray(cfg.chart_data_label_))
                        cfg.chart_data_label_ = [cfg.chart_data_label_];

                    if (cfg.chart_var_field_) {
                        lChartData.labels = [];
                        lChartData.datasets = [];

                        for (let d = 0; d < cfg.chart_data_field_.length; d++) {
                            const dataField = cfg.chart_data_field_[d];
                            const dataLabel = cfg.chart_data_label_[d];
                            const ds = { label: dataLabel, data: [] };

                            for (let i = 0; i < lData.length; i++) {
                                const rec = lData[i];
                                ds.data.push(rec[dataField]);

                                if (d === 0) {
                                    lChartData.labels.push(rec[cfg.chart_var_field_]);
                                }
                            }

                            lChartData.datasets.push(ds);
                        }
                    }
                }
            }
        }

        const lOptions =
        {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins:
            {
                legend:
                {
                    labels:
                    {
                        fontColor: lTextColor
                    }
                }
            },
            scales:
            {
                x:
                {
                    ticks:
                    {
                        font:
                        {
                            weight: 600
                        }
                    },
                    grid:
                    {
                        display: true,
                        drawBorder: true
                    }
                },
                y:
                {
                    grid:
                    {
                        color: lSurfaceBorder,
                        drawBorder: false
                    }
                }
            }
        }

        setChartData(lChartData);
        setChartOptions(lOptions);
    }, [lProps.pDataCollection, lProps.pData, lProps.pChartData, lProps.pReportChartProperties]);

    return (
        // <div className="card">
            <Chart
                type="bar"
                data={mChartData}
                options={mChartOptions} className='p-2' />
        // </div>
    )
}
